import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  APP_THEME_EVENT,
  APP_THEME_STORAGE_KEY,
  getStoredAppTheme,
  setStoredAppTheme
} from '../../utils/theme';

const ThemeToggle = ({ className = '', size = 'md', shortLabels = false }) => {
  const { i18n } = useTranslation();
  const [theme, setTheme] = useState(getStoredAppTheme);

  const isLight = theme === 'light';
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const isSmall = size === 'sm';
  const containerSizeClass = isSmall ? 'h-[32px] p-0.5 rounded-md' : 'h-[40px] p-1 rounded-lg';
  const indicatorInsetClass = isSmall ? 'top-0.5 bottom-0.5' : 'top-1 bottom-1';
  const indicatorWidthClass = isSmall ? 'w-[calc(50%-2px)]' : 'w-[calc(50%-4px)]';
  const labelSizeClass = isSmall ? 'text-[9px]' : 'text-[10px]';
  const darkLabel = shortLabels ? (isEnglish ? 'D' : 'Т') : (isEnglish ? 'Dark' : 'Темная');
  const lightLabel = shortLabels ? (isEnglish ? 'L' : 'Б') : (isEnglish ? 'Light' : 'Белая');

  useEffect(() => {
    const handleThemeChange = (event) => {
      setTheme(event.detail?.theme === 'light' ? 'light' : 'dark');
    };
    const handleStorageChange = (event) => {
      if (event.key === APP_THEME_STORAGE_KEY) {
        setTheme(event.newValue === 'light' ? 'light' : 'dark');
      }
    };

    window.addEventListener(APP_THEME_EVENT, handleThemeChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener(APP_THEME_EVENT, handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const setNextTheme = (nextTheme) => {
    setTheme(nextTheme);
    setStoredAppTheme(nextTheme);
  };

  return (
    <div
      data-segmented-toggle="true"
      className={`relative w-full bg-slate-800/40 border border-slate-700/30 flex items-center ${containerSizeClass} ${className}`}
    >
      <div
        className={`absolute ${indicatorInsetClass} ${indicatorWidthClass} bg-blue-600 rounded-md transition-all duration-300 ease-in-out shadow-lg shadow-blue-900/10
          ${isLight ? 'translate-x-[100%]' : 'translate-x-0'}`}
      />

      <button
        type="button"
        onClick={() => setNextTheme('dark')}
        aria-label={isEnglish ? 'Dark theme' : 'Темная тема'}
        className={`flex-1 relative z-10 ${labelSizeClass} font-bold transition-colors duration-300 uppercase tracking-wider
          ${!isLight ? 'text-white' : 'text-slate-500 hover:text-slate-400'}`}
      >
        {darkLabel}
      </button>

      <button
        type="button"
        onClick={() => setNextTheme('light')}
        aria-label={isEnglish ? 'Light theme' : 'Белая тема'}
        className={`flex-1 relative z-10 ${labelSizeClass} font-bold transition-colors duration-300 uppercase tracking-wider
          ${isLight ? 'text-white' : 'text-slate-500 hover:text-slate-400'}`}
      >
        {lightLabel}
      </button>
    </div>
  );
};

export default ThemeToggle;
